import { ApiService } from '../../../services/api';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { CONSTANTS } from '../../../utils/constants';
import { AppDispatch } from '../../type';
import { TableActions } from '../table/table.actions';
import { getErrorMessages } from '../../../utils/formatters';
import { IApiError } from '../../../models/api';
import { GetCustomerAssignmentsPayload } from './type';
import { ICustomerAssignment } from '../../../models/customerAssignment';

const { get, post, deleteApi, put } = ApiService;

enum CustomerAssignmentsApiEndpoints {
  GET_CUSTOMER_ASSIGNMENTS = `CustomerAssignment/Get`,
  ADD_RANGE_CUSTOMER_ASSIGNMENT = `CustomerAssignment/AddRange`,
  REMOVE_CUSTOMER_ASSIGNMENT = `CustomerAssignment/Remove`,
  UPDATE_CUSTOMER_ASSIGNMENT = `CustomerAssignment/Update`,
}

enum CustomerAssignmentsActionsEnum {
  GET_CUSTOMER_ASSIGNMENTS = 'GET_CUSTOMER_ASSIGNMENTS',
  ADD_RANGE_CUSTOMER_ASSIGNMENT = 'ADD_RANGE_CUSTOMER_ASSIGNMENTS',
  REMOVE_CUSTOMER_ASSIGNMENT = 'REMOVE_CUSTOMER_ASSIGNMENT',
  UPDATE_CUSTOMER_ASSIGNMENT = 'UPDATE_CUSTOMER_ASSIGNMENT',
}

export const CustomerAssignmentsActionsNames = {
  [CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS]:
    generateAsyncActionNames(
      CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS
    ),
  [CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT]:
    generateAsyncActionNames(
      CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT
    ),
  [CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT]:
    generateAsyncActionNames(
      CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT
    ),
  [CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT]:
    generateAsyncActionNames(
      CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT
    ),
};

const Actions = {
  [CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS]: {
    START: () => ({
      type: CustomerAssignmentsActionsNames.GET_CUSTOMER_ASSIGNMENTS.START,
    }),
    FULFILLED: (data: GetCustomerAssignmentsPayload) => ({
      type: CustomerAssignmentsActionsNames.GET_CUSTOMER_ASSIGNMENTS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: CustomerAssignmentsActionsNames.GET_CUSTOMER_ASSIGNMENTS.REJECTED,
      payload: error,
    }),
  },
  [CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT]: {
    START: () => ({
      type: CustomerAssignmentsActionsNames.ADD_RANGE_CUSTOMER_ASSIGNMENTS
        .START,
    }),
    FULFILLED: (data: any) => ({
      type: CustomerAssignmentsActionsNames.ADD_RANGE_CUSTOMER_ASSIGNMENTS
        .FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: CustomerAssignmentsActionsNames.ADD_RANGE_CUSTOMER_ASSIGNMENTS
        .REJECTED,
      payload: error,
    }),
  },
  [CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT]: {
    START: () => ({
      type: CustomerAssignmentsActionsNames.REMOVE_CUSTOMER_ASSIGNMENT.START,
    }),
    FULFILLED: (data: { phoneNumber: string }) => ({
      type: CustomerAssignmentsActionsNames.REMOVE_CUSTOMER_ASSIGNMENT
        .FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: CustomerAssignmentsActionsNames.REMOVE_CUSTOMER_ASSIGNMENT.REJECTED,
      payload: error,
    }),
  },
  [CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT]: {
    START: () => ({
      type: CustomerAssignmentsActionsNames.UPDATE_CUSTOMER_ASSIGNMENT.START,
    }),
    FULFILLED: (data: { updated: ICustomerAssignment }) => ({
      type: CustomerAssignmentsActionsNames.UPDATE_CUSTOMER_ASSIGNMENT
        .FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: CustomerAssignmentsActionsNames.UPDATE_CUSTOMER_ASSIGNMENT.REJECTED,
      payload: error,
    }),
  },
};

const getCustomerAssignments =
  (
    params?: any,
    sort?: any,
    updateTable = true,
    pageNumber: number = CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
    pageSize?: number | null
  ) =>
  async (dispatch: AppDispatch, getState?: any) => {
    dispatch(
      Actions[CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS].START()
    );

    const requestData: any = {
      params,
      headers: {
        'X-Pagination': JSON.stringify({
          PageNumber: pageNumber,
          PageSize:
            pageSize ?? getState()?.table.pageSize ?? CONSTANTS.GRIDS.PAGE_SIZE,
        }),
      },
    };

    if (sort && sort.length) {
      requestData.headers['X-Sorting'] = JSON.stringify({
        Sortings: sort,
      });
    }

    try {
      const { data }: any = await get(
        CustomerAssignmentsApiEndpoints.GET_CUSTOMER_ASSIGNMENTS,
        requestData
      );

      const customerAssignments = data?.content?.customerAssignments;

      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS
        ].FULFILLED(customerAssignments)
      );

      if (updateTable) {
        dispatch(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount,
            pageNumber: CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
      return customerAssignments;
    } catch (error: any) {
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.GET_CUSTOMER_ASSIGNMENTS
        ].REJECTED(getErrorMessages(error))
      );
    }
  };

const insertCustomerAssignmentsRange =
  (body: any) => async (dispatch: AppDispatch) => {
    dispatch(
      Actions[
        CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT
      ].START()
    );
    try {
      const { data }: any = await post(
        CustomerAssignmentsApiEndpoints.ADD_RANGE_CUSTOMER_ASSIGNMENT,
        body
      );
      const content = data?.content ?? {};
      if (!data.isSucceeded) {
        throw Error(JSON.stringify(data.errors));
      }
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT
        ].FULFILLED(content)
      );
      return content;
    } catch (error: any) {
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.ADD_RANGE_CUSTOMER_ASSIGNMENT
        ].REJECTED(getErrorMessages(error))
      );
    }
  };

const removeCustomerAssignment =
  (phoneNumber: string) => async (dispatch: AppDispatch) => {
    dispatch(
      Actions[CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT].START()
    );
    try {
      const { data }: any = await deleteApi(
        `${CustomerAssignmentsApiEndpoints.REMOVE_CUSTOMER_ASSIGNMENT}/${phoneNumber}`
      );
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT
        ].FULFILLED({
          phoneNumber,
        })
      );
    } catch (error: any) {
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.REMOVE_CUSTOMER_ASSIGNMENT
        ].REJECTED(getErrorMessages(error))
      );
    }
  };

const updateCustomerAssignment =
  (
    phoneNumber: string,
    body: {
      fullNameFromContractor: string | null;
      siteId: number;
      lockerNumber?: string | null;
      voucherExpiryDate?: string | null;
      sbSubscriptionVoucherDurationInMonths?: number | null;
      upgradeToSmartLockerVoucherDurationInMonths?: number | null;
    }
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      Actions[CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT].START()
    );
    try {
      const { data }: any = await put(
        `${CustomerAssignmentsApiEndpoints.UPDATE_CUSTOMER_ASSIGNMENT}/${phoneNumber}`,
        body
      );
      const content = data?.content ?? {};
      if (!data.isSucceeded) {
        throw Error(JSON.stringify(data.errors));
      }
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT
        ].FULFILLED(content)
      );
      return content;
    } catch (error: any) {
      dispatch(
        Actions[
          CustomerAssignmentsActionsEnum.UPDATE_CUSTOMER_ASSIGNMENT
        ].REJECTED(getErrorMessages(error))
      );
    }
  };

export const CustomerAssignmentsActions = {
  getCustomerAssignments,
  insertCustomerAssignmentsRange,
  removeCustomerAssignment,
  updateCustomerAssignment,
};
